import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { toast } from "../stores/toast";
import { Button, PageLoader } from "../components/ui";

export function FlexxiKycCapture() {
  const { token } = useParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["flexxi-kyc-capture", token],
    queryFn: () =>
      api<{
        fieldLabel: string;
        status: string;
        completed: boolean;
      }>(`/flexxi/kyc/capture/${token}`),
    enabled: Boolean(token),
  });

  async function uploadFile(file: File) {
    if (!token) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api(`/flexxi/kyc/capture/${token}/upload`, { formData });
      setDone(true);
      toast("success", "Upload complete — it will appear on your computer shortly");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  if (isLoading) return <PageLoader />;
  if (isError || !data) {
    return (
      <div className="mx-auto max-w-md p-6 text-center text-sm text-white/50">
        This capture link is invalid or expired.
      </div>
    );
  }

  if (data.status === "EXPIRED") {
    return (
      <div className="mx-auto max-w-md space-y-3 p-6 text-center">
        <h1 className="font-display text-lg font-bold">Link expired</h1>
        <p className="text-sm text-white/50">Generate a new QR code on your computer and scan again.</p>
      </div>
    );
  }

  const captureFace = data.fieldLabel.toLowerCase().includes("selfie");

  return (
    <div className="mx-auto min-h-dvh max-w-md space-y-6 px-4 py-8">
      <div>
        <h1 className="font-display text-lg font-bold">FlexxiPlan verification</h1>
        <p className="mt-1 text-sm text-white/50">{data.fieldLabel}</p>
      </div>

      {done || data.completed ? (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
          <p className="font-semibold text-emerald-200">Upload received</p>
          <p className="mt-2 text-sm text-white/50">You can close this page — your computer will update automatically.</p>
        </div>
      ) : (
        <>
          <p className="text-xs leading-relaxed text-white/45">
            Take a clear photo in good light. Keep all edges inside the frame. Do not upload screenshots.
          </p>
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="flex w-full flex-col items-center gap-2 rounded-2xl border border-dashed border-white/15 py-12 text-sm text-white/45 hover:border-brand-pink/40 disabled:opacity-50"
          >
            <Upload size={28} />
            {uploading ? "Uploading…" : "Take photo or choose file"}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture={captureFace ? "user" : "environment"}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void uploadFile(file);
            }}
          />
        </>
      )}

      <Button variant="ghost" className="w-full text-xs" onClick={() => window.close()}>
        Close
      </Button>
    </div>
  );
}
